/* eslint-disable react-hooks/exhaustive-deps */
'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import dayjs from 'dayjs';

import { clearDatadogUser, initDatadogRum, tagDatadogUser } from '@/lib/datadog';

initDatadogRum();
import { usePathname, useSearchParams } from 'next/navigation';
import { SessionProvider, signOut, useSession } from 'next-auth/react';
import { NuqsAdapter } from 'nuqs/adapters/next/app';
import React, { useEffect, useRef, useState } from 'react';
import toast, { ToastBar, Toaster } from 'react-hot-toast';
import useCookie from 'react-use-cookie';
import { useLocalStorage, useWindowSize } from 'usehooks-ts';

import useHashChUserId from '@/api-hooks/useHashChUserId';
import { useUser } from '@/api-hooks/useUser';
import { useUserPlan } from '@/api-hooks/useUserPlan';
import { channelTalkPluginKey } from '@/consts/channeltalk';
import { isProduction } from '@/consts/env';
import { protectedRoutes } from '@/consts/route';
import MobileChannelTalk from '@/features/ChannelTalk/MobileChannelTalk';
import useChannelIOApi from '@/hooks/useChannelIOApi';
import { useIsOnline, useIsOnlineWrapper } from '@/hooks/useIsOnline';
import { useDesktop } from '@/hooks/useMobile';
import { usePrevUrl } from '@/hooks/usePrevUrl';
import { shouldRetryQuery } from '@/lib/query-retry';
import { GrsSessionProvider } from '@/providers/GrsSessionProvider';
import { TenantProvider, usePartnerConfig } from '@/providers/TenantProvider';
import { AlertProvider } from '@/store/alertContext';
import { ConfirmProvider } from '@/store/confirmContext';
import { FeatureFlagProvider } from '@/store/FeatureFlagContext';

import LandingCampaignGtmChannel from './LandingCampaignGtmChannel';
import SandboxRedirect from './SandboxRedirect';

type sessionProps = {
  children: React.ReactNode;
};
function ClientProvider({ children }: sessionProps) {
  const pathname = usePathname();

  useIsOnlineWrapper();

  const avoidPageList = ['/special', '/event', '/onboarding/mate'];

  return (
    <>
      {/* <ServiceUnavailableRedirect /> */}
      <SessionProvider refetchOnWindowFocus refetchInterval={300}>
        <SessionRefresh />
        <SavePrevUrl />
        <NuqsAdapter>
          <QueryProvider>
            {avoidPageList.some((page) => pathname.startsWith(page)) ? (
              <></>
            ) : (
              <Toaster toastOptions={{ duration: 3000 }}>
                {(t) => (
                  <div onClick={() => toast.dismiss(t.id)} className="cursor-pointer">
                    <ToastBar toast={t}>
                      {({ icon, message }) => (
                        <>
                          {icon}
                          {message}
                        </>
                      )}
                    </ToastBar>
                  </div>
                )}
              </Toaster>
            )}
            <TenantProvider>
              <SandboxRedirect />
              <LandingCampaignGtmChannel />
              <GrsSessionProvider>
                <FeatureFlagProvider>
                  <AlertProvider>
                    <ConfirmProvider>
                      {children}

                      <ChannelTalkGuard />
                      <MobileChannelTalk />

                      <ClarityClient />
                    </ConfirmProvider>
                  </AlertProvider>
                </FeatureFlagProvider>
              </GrsSessionProvider>
            </TenantProvider>
          </QueryProvider>
        </NuqsAdapter>
      </SessionProvider>
    </>
  );
}

const SessionRefresh = () => {
  const { data: session, status, update } = useSession();
  const [userCookie, setUserCookie, removeCookie] = useCookie('user_id', '');
  const [retryCount, setRetryCount] = useState(0);
  const retryLimit = 3;
  const config = usePartnerConfig();
  const isSessionStorage = config.auth.strategy === 'session-storage';

  useEffect(() => {
    // GRS iframe은 GrsSessionProvider가 토큰 갱신을 처리하므로 스킵
    if (isSessionStorage) return;

    if (status === 'authenticated') {
      if (session.refreshTokenExpiresIn < Date.now()) {
        signOut({ redirect: false });
        return;
      }

      if (session.accessTokenExpiresIn < Date.now()) {
        if (retryCount < retryLimit) {
          update();
          setRetryCount(retryCount + 1);
        } else {
          console.error('Retry limit reached. Please try again later.');
        }
      }
    }
  }, [session, status, retryCount, isSessionStorage]);

  useEffect(() => {
    // GRS iframe은 쿠키 사용 안함
    if (isSessionStorage) return;

    const updateGtmDataLayer = (userId) => {
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({
        event: 'userIdUpdated',
        userId: userId,
      });
    };

    if (session?.user?.user_id) {
      setUserCookie(session.user.user_id);
      updateGtmDataLayer(session.user.user_id);

      // 모든 로그인 유저를 Datadog에 식별 (@usr.id로 검색 가능)
      tagDatadogUser({
        user_id: session.user.user_id,
        email: session.user.email,
        auth_method: session.user.auth_method,
      });
    } else {
      if (userCookie === '' || userCookie === undefined) return;
      removeCookie();
      clearDatadogUser();
    }
  }, [session, isSessionStorage]);

  return <></>;
};

const QueryProvider = ({ children }: { children: React.ReactNode }) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: true,
            refetchOnMount: true,
            refetchOnReconnect: true,
            retry: shouldRetryQuery,
            staleTime: 1000 * 60 * 5,
          },
        },
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      {!isProduction && <ReactQueryDevtools initialIsOpen={false} />}
      {children}
    </QueryClientProvider>
  );
};

const ChannelTalkGuard = () => {
  const pathname = usePathname();

  if (pathname.startsWith(protectedRoutes.root)) return null;
  if (pathname.startsWith('/auth/lottegrs/magic-link')) return null;
  return <ChannelTalk />;
};

const ChannelTalk = () => {
  const pathname = usePathname();
  const session = useSession();
  const isOnline = useIsOnline();
  const { data: userData, refetch: refetchUser } = useUser();
  const [hashedUserId, setHashedUserId] = useLocalStorage('ch_h_uid', '');
  const [userId, setUserId] = useLocalStorage('ch_uid', '');
  const [, setBadge] = useLocalStorage('ch_badge', '');

  const { width } = useWindowSize();
  const isDesktop = useDesktop();

  const {
    loadScript,
    boot,
    removeTags,
    addTags,
    updateUser,
    setPage,
    onBadgeChanged,
    onShowMessenger,
    onHideMessenger,
    isChannelInitialized,
    setIsChannelInitialized,

    setIsChannelUIOpen,
  } = useChannelIOApi();

  const { mutateAsync } = useHashChUserId();

  // 부트 상태 추적
  const [isBooted, setIsBooted] = useState(false);
  const [lastBootedDesktopState, setLastBootedDesktopState] = useState<boolean | null>(null);

  // 1. 스크립트 로드 및 초기 부트 (한 번만)
  // 첫 상호작용(포인터·스크롤·키 입력) 시점까지 지연한다.
  // idle 시점 로드는 Lighthouse 측정 구간 안에 들어와 채널톡 서드파티 쿠키(x-veil-id)로
  // Best Practices 가 감점되고 TBT 도 늘어난다.
  useEffect(() => {
    if (isChannelInitialized) return;

    const events = [
      'pointerdown',
      'pointermove',
      'touchstart',
      'keydown',
      'wheel',
      'scroll',
    ] as const;
    const listenerOptions: AddEventListenerOptions = { capture: true, passive: true };

    const removeListeners = () => {
      events.forEach((event) => window.removeEventListener(event, init, listenerOptions));
    };

    function init() {
      removeListeners();
      loadScript();
      setIsChannelInitialized(true);
    }

    events.forEach((event) => window.addEventListener(event, init, listenerOptions));

    return removeListeners;
  }, [isChannelInitialized, loadScript, setIsChannelInitialized]);

  // 2. 사용자 데이터가 없을 때 강제로 가져오기
  useEffect(() => {
    if (session.status === 'authenticated' && !userData) {
      refetchUser();
    }
  }, [session.status, userData, refetchUser]);

  // 3. 사용자 ID 해싱 처리
  useEffect(() => {
    if (!userData?.user_id) return;
    if (userId === userData.user_id && hashedUserId !== '') return;

    mutateAsync(userData.user_id).then((res) => {
      if (res?.hashed_user_id) {
        setHashedUserId(res.hashed_user_id);
        setUserId(userData.user_id);
      }
    });
  }, [userData?.user_id]);

  // 4. 초기 부트 및 isDesktop 변경 시 재부트
  useEffect(() => {
    if (!isChannelInitialized || !isOnline || width < 100) return;

    // 이미 부트되어 있고, isDesktop 상태가 변경되지 않았으면 부트하지 않음
    if (isBooted && lastBootedDesktopState === isDesktop) {
      return;
    }

    boot({
      pluginKey: channelTalkPluginKey,
      language: 'ko',
      memberHash: hashedUserId || undefined,
      memberId: userId || undefined,
      hideChannelButtonOnBoot: isDesktop ? false : true,
      zIndex: isDesktop ? 43 : 100000,
    });

    setIsBooted(true);
    setLastBootedDesktopState(isDesktop);
  }, [
    isChannelInitialized,
    isOnline,
    width,
    isDesktop,
    hashedUserId,
    userId,
    boot,
    isBooted,
    lastBootedDesktopState,
  ]);

  // 5. ChannelTalk UI 열림/닫힘 추적
  useEffect(() => {
    if (!isChannelInitialized) return;

    onShowMessenger(() => {
      setIsChannelUIOpen(true);
    });

    onHideMessenger(() => {
      setIsChannelUIOpen(false);
    });
  }, [isChannelInitialized, onShowMessenger, onHideMessenger, setIsChannelUIOpen]);

  // 6. 사용자 정보 업데이트 (별도 함수 사용)
  useEffect(() => {
    if (!isChannelInitialized || !userData) return;

    updateUser({
      profile: {
        name: userData.name || null,
        email: userData.email || null,
        mobileNumber: userData.phone_number || null,
      },
      unsubscribeEmail: !!userData.is_advertise_term_agree,
      unsubscribeTexting: !!userData.is_advertise_term_agree,
    });
  }, [isChannelInitialized, userData, updateUser]);

  // 7. 구독 태그 관리
  useEffect(() => {
    if (!isChannelInitialized || !userData) return;

    const userSubscribe = userData?.subscription?.plan?.name || '무료';

    if (userSubscribe === '무료') {
      updateUser({
        tags: ['구독없음'],
      });
    } else {
      removeTags(['구독없음']);
      addTags([userSubscribe]);
    }
  }, [isChannelInitialized, userData, updateUser, removeTags, addTags]);

  // 8. 페이지 변경 추적
  useEffect(() => {
    if (!isChannelInitialized || !window?.location) return;

    const location = window.location.toString();
    setPage(location);
  }, [isChannelInitialized, pathname, setPage]);

  // 9. 배지 변경 콜백 설정
  useEffect(() => {
    if (!isChannelInitialized || !window) return;

    onBadgeChanged((unread) => {
      setBadge(unread.toString());
    });
  }, [isChannelInitialized, onBadgeChanged, setBadge]);

  return <></>;
};

const MODAL_PHASE_KEYS = ['p_phase', 'pr_phase', 'pc_phase', 'pd_phase', 'pu_phase', 'bc_phase'];

const SavePrevUrl = () => {
  const [, setPrevUrl] = usePrevUrl();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  useEffect(() => {
    if (!pathname.includes('/auth')) {
      const cleanParams = new URLSearchParams(searchParams.toString());
      MODAL_PHASE_KEYS.forEach((key) => cleanParams.delete(key));
      const query = cleanParams.toString();
      setPrevUrl(`${pathname}${query ? `?${query}` : ''}`);
    }
  }, [pathname, searchParams, setPrevUrl]);

  return <></>;
};

const ClarityClient = () => {
  const { data: userData } = useUser();
  const { data: userPlan } = useUserPlan();
  const lastSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    let attempt = 0;
    const maxAttempts = 20; // 500ms * 20 ≈ 10초
    let timer: number | null = null;
    let cancelled = false;

    const buildPayload = () => {
      // device 탐지
      let device = 'desktop';
      if (typeof navigator !== 'undefined') {
        const ua = navigator.userAgent;
        const isTablet =
          /Tablet|iPad/i.test(ua) ||
          (/(Macintosh)/.test(ua) && (navigator as any).maxTouchPoints > 1);
        if (/Mobi|Android/i.test(ua) && !isTablet) device = 'mobile';
        else if (isTablet) device = 'tablet';
      }

      if (!userData) {
        return { device };
      }

      // userTenure 계산
      let userTenure = 'unknown';
      if (userData.created_at) {
        const created = dayjs(userData.created_at);
        if (created.isValid()) {
          const diff = dayjs().diff(created, 'day');
          if (diff <= 7) userTenure = 'new';
          else if (diff <= 30) userTenure = 'recent';
          else if (diff <= 90) userTenure = 'active';
          else userTenure = 'loyal';
        }
      }

      // age_group 계산
      let age_group = 'unknown';
      if (userData.birth_date && userData.birth_date.length >= 4) {
        const year = Number(userData.birth_date.slice(0, 4));
        if (!Number.isNaN(year) && year > 1900 && year <= dayjs().year()) {
          const age = dayjs().year() - year;
          if (age < 20) age_group = '10s';
          else if (age < 30) age_group = '20s';
          else if (age < 40) age_group = '30s';
          else if (age < 50) age_group = '40s';
          else if (age < 60) age_group = '50s';
          else age_group = '60+';
        }
      }

      const gender =
        userData.genderNumber == null
          ? 'unknown'
          : userData.genderNumber % 2 === 0
            ? 'female'
            : 'male';

      // plan_name 보정 (문자열 안전 추출)
      const plan_name =
        typeof userPlan === 'string'
          ? userPlan
          : (userPlan as any)?.plan?.name || (userData?.subscription?.plan?.name ?? 'unknown');

      return {
        auth_method: userData.auth_method ?? 'unknown',
        plan_name,
        userTenure,
        service_term_agree: !!userData.service_term_agree_date,
        advertise_term_agree: !!userData.is_advertise_term_agree,
        gender,
        birth_date: userData.birth_date ?? '',
        age_group,
        device,
      };
    };

    const push = () => {
      if (cancelled) return;
      const clarity = (window as any)?.clarity;
      if (!clarity) {
        attempt++;
        if (attempt <= maxAttempts) {
          timer = window.setTimeout(push, 500);
        }
        return;
      }
      const payload = buildPayload();
      const signature = JSON.stringify(payload);
      if (lastSignatureRef.current === signature) return; // 중복 전송 방지
      lastSignatureRef.current = signature;
      clarity('set', payload);
    };

    // 초기 약간 지연 후 실행
    timer = window.setTimeout(push, 300);

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [userData, userPlan]);

  return null;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars

export default ClientProvider;
